import React from 'react';
import { Link } from 'react-router-dom';
import { Column } from '../../components/DataTable/DataTable';
import Badge from '../../components/ui/Badge';
import { FieldConfig } from '../../components/generic/fieldRenderers';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../config/appConfig';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { EyeIcon, PencilIcon } from '../../components/ui/Icons';
import type { MgaPeriod, MgaPeriodStatus } from './useData';

export const formatMgaStatus = (status: MgaPeriodStatus) => {
  switch (status) {
    case 'DRAFT':
      return 'Draft';
    case 'OPEN':
      return 'Open';
    case 'CLOSED':
      return 'Closed';
    default:
      return status;
  }
};

export const formatMgaCloseApprovalStatus = (
  status: NonNullable<MgaPeriod['closeApprovalStatus']> | null | undefined,
) => {
  switch (status) {
    case 'DRAFT':
      return 'Approval draft';
    case 'PENDING':
      return 'Awaiting approval';
    case 'APPROVED':
      return 'Close approved';
    case 'REJECTED':
      return 'Close rejected';
    case 'CANCELLED':
      return 'Approval cancelled';
    default:
      return null;
  }
};

export const closeApprovalBadgeVariant = (
  status: NonNullable<MgaPeriod['closeApprovalStatus']> | null | undefined,
): 'secondary' | 'warning' | 'success' | 'danger' | 'info' => {
  if (status === 'PENDING') return 'warning';
  if (status === 'APPROVED') return 'success';
  if (status === 'REJECTED') return 'danger';
  if (status === 'CANCELLED') return 'secondary';
  if (status === 'DRAFT') return 'info';
  return 'secondary';
};

export const statusBadgeVariant = (status: MgaPeriodStatus): 'secondary' | 'success' | 'danger' => {
  if (status === 'OPEN') return 'success';
  if (status === 'CLOSED') return 'danger';
  return 'secondary';
};

export const moduleConfig = {
  module: {
    title: 'MGA',
    permissionModuleKey: 'mga',
    route: {
      list: '/mga',
      new: '/mga/new',
      detail: (id: number) => `/mga/${id}`,
      sheet: (id: number) => `/mga/${id}/sheet`,
      edit: (id: number) => `/mga/${id}/sheet`},
    api: {
      list: '/mga/periods',
      detail: (id: number) => `/mga/periods/${id}`}},
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions},
  search: { placeholder: 'Search by vessel…' },
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '',
      fields: ['status', 'vessel', 'year', 'month']},
  ],
  filters: {
    fields: [
      {
        key: 'status',
        label: 'Status',
        type: 'select' as const,
        getValue: (item: MgaPeriod) => item.status,
        options: [
          { value: 'DRAFT', label: 'Draft' },
          { value: 'OPEN', label: 'Open' },
          { value: 'CLOSED', label: 'Closed' },
        ]},
      {
        key: 'vessel',
        label: 'Vessel',
        placeholder: 'All vessels',
        getValue: (item: MgaPeriod) => item.vessel?.vesselName || '',
        options: [] as Array<{ value: string; label: string }>,
        apiEndpoint: '/vessels?page=1&limit=500',
        labelField: 'vesselName'},
      {
        key: 'year',
        label: 'Year',
        placeholder: 'All years',
        getValue: (item: MgaPeriod) => {
          const d = item.periodFrom ? new Date(item.periodFrom) : null;
          return d && !Number.isNaN(d.getTime()) ? String(d.getUTCFullYear()) : '';
        },
        options: (() => {
          const y = new Date().getUTCFullYear();
          const opts: Array<{ value: string; label: string }> = [];
          for (let i = y + 1; i >= y - 5; i -= 1) opts.push({ value: String(i), label: String(i) });
          return opts;
        })()},
      {
        key: 'month',
        label: 'Month',
        placeholder: 'All months',
        getValue: (item: MgaPeriod) => {
          const d = item.periodFrom ? new Date(item.periodFrom) : null;
          return d && !Number.isNaN(d.getTime()) ? String(d.getUTCMonth() + 1) : '';
        },
        options: [
          { value: '1', label: 'January' },
          { value: '2', label: 'February' },
          { value: '3', label: 'March' },
          { value: '4', label: 'April' },
          { value: '5', label: 'May' },
          { value: '6', label: 'June' },
          { value: '7', label: 'July' },
          { value: '8', label: 'August' },
          { value: '9', label: 'September' },
          { value: '10', label: 'October' },
          { value: '11', label: 'November' },
          { value: '12', label: 'December' },
        ]},
    ],
    applyFilter: (items: MgaPeriod[], filters: Record<string, string[]>) => {
      return items.filter((item) => {
        if (filters.status?.length && !filters.status.includes(item.status)) return false;
        if (filters.vessel?.length) {
          const name = item.vessel?.vesselName || '';
          if (!filters.vessel.includes(name)) return false;
        }
        const from = item.periodFrom ? new Date(item.periodFrom) : null;
        if (filters.year?.length && from && !Number.isNaN(from.getTime())) {
          if (!filters.year.includes(String(from.getUTCFullYear()))) return false;
        }
        if (filters.month?.length && from && !Number.isNaN(from.getTime())) {
          if (!filters.month.includes(String(from.getUTCMonth() + 1))) return false;
        }
        return true;
      });
    }},
  toolbar: {
    actions: [
      {
        key: 'posting',
        icon: 'settings',
        title: 'Posting settings',
        showTitle: false,
        variant: 'default' as const,
        href: '/mga/settings/posting'},
      {
        key: 'add',
        icon: 'add',
        title: 'New MGA period',
        showTitle: false,
        variant: 'primary' as const,
        href: '/mga/new'},
    ]},
  table: {
    actionsPosition: 'left' as const,
    keyExtractor: (item: MgaPeriod) => item.id,
  },
  emptyStates: {
    noData: 'Create an MGA period for a vessel and month.',
    noSearchResults: 'No MGA periods match your search.',
    noFilterResults: 'No MGA periods match the filters.',
  },
  form: {
    new: {
      title: 'New MGA period'}},
  delete: {
    title: 'Delete MGA period',
    message: 'Delete this MGA period and all crew lines?',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Could not delete MGA period.',
    handler: async (id: number) => {
      const { deleteMgaPeriod } = await import('../../services/mutations');
      await deleteMgaPeriod(id);
    }},
  utils: {
    formatDate: (dateString?: string) => (dateString ? formatDateGlobal(dateString) : '')},
  detail: {
    subtitle: 'MGA period',
    notFoundTitle: 'Not found',
    notFoundMessage: 'This MGA period does not exist.',
    notFoundBackText: 'Back to list',
    defaultTitle: 'MGA period',
    getTitle: (item: MgaPeriod) => {
      const vessel = item.vessel?.vesselName ?? `Vessel #${item.vesselId}`;
      return `${vessel} — ${formatDateGlobal(item.periodFrom)} → ${formatDateGlobal(item.periodTo)}`;
    }},
  fields: [] as FieldConfig[]};

const TextCell: React.FC<{ value: string | null | undefined }> = ({ value }) => {
  const { colors } = useTheme();
  return <span className={cn('text-sm whitespace-nowrap', colors.text)}>{value || ''}</span>;
};

const CountCell: React.FC<{ value: number }> = ({ value }) => {
  const { colors } = useTheme();
  return <span className={cn('text-sm tabular-nums', colors.text)}>{value}</span>;
};

const AmtCell: React.FC<{ value: number | null | undefined; emphasize?: boolean }> = ({
  value,
  emphasize}) => {
  const { colors } = useTheme();
  const n = value == null ? null : Number(value);
  const text =
    n == null || Number.isNaN(n)
      ? '—'
      : n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (
    <span
      className={cn(
        'text-sm tabular-nums whitespace-nowrap',
        emphasize ? 'font-semibold' : null,
        colors.text,
      )}
    >
      {text}
    </span>
  );
};

export const getColumns = (formatDate: (dateString?: string) => string): Column<MgaPeriod>[] => [
  {
    key: 'vessel.vesselName',
    header: 'Vessel',
    sortable: true,
    render: (_v, row) => <TextCell value={row.vessel?.vesselName} />},
  {
    key: 'vessel.vesselAbrv',
    header: 'Code',
    sortable: true,
    render: (_v, row) => <TextCell value={row.vessel?.vesselAbrv} />},
  {
    key: 'periodFrom',
    header: 'From',
    sortable: true,
    render: (v) => formatDate(v)},
  {
    key: 'periodTo',
    header: 'To',
    sortable: true,
    render: (v) => formatDate(v)},
  {
    key: 'currency',
    header: 'Curr.',
    sortable: true,
    render: (v) => <TextCell value={v} />},
  {
    key: 'status',
    header: 'Status',
    sortable: true,
    render: (v, row) => {
      const status = (v ?? row?.status) as MgaPeriodStatus;
      return (
        <Badge variant={statusBadgeVariant(status)} className="shrink-0 border rounded-md px-2 py-0.5">
          {formatMgaStatus(status)}
        </Badge>
      );
    }},
  {
    key: '_count.lines',
    header: 'Crew',
    sortable: false,
    align: 'right',
    render: (_v, row) => (
      <CountCell value={row._count?.lines ?? row.lines?.length ?? 0} />
    )},
  {
    key: 'totals.netDue',
    header: 'Net due',
    sortable: false,
    align: 'right',
    render: (_v, row) => <AmtCell value={row.totals?.netDue} emphasize />},
  {
    key: 'cashOnHand',
    header: 'Cash on hand',
    sortable: false,
    align: 'right',
    render: (_v, row) => <AmtCell value={row.cashOnHand} />},
  {
    key: 'bondedBalance',
    header: 'Bonded balance',
    sortable: false,
    align: 'right',
    render: (_v, row) => <AmtCell value={row.bondedBalance} />},
  {
    key: '_count.crewRequests',
    header: 'Requests',
    sortable: false,
    align: 'right',
    render: (_v, row) => <CountCell value={row._count?.crewRequests ?? 0} />},
  {
    key: 'updatedAt',
    header: 'Updated',
    sortable: true,
    render: (v) => formatDate(v)},
];

const TableActionsCell: React.FC<{
  item: MgaPeriod;
  onDelete: (id: number) => void;
  canDelete?: boolean;
  canEdit?: boolean;
}> = ({ item, onDelete, canDelete = true, canEdit = true }) => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  return (
  <div className="flex items-center flex-nowrap">
    <Link
      to={moduleConfig.module.route.detail(item.id)}
      className={cn('p-1 rounded-lg transition-colors', colors.primaryFg, colors.interactiveHover)}
      onClick={(e) => e.stopPropagation()}
      title="Dashboard"
    >
      <EyeIcon className="w-5 h-5" />
    </Link>
    {canEdit && (
      <Link
        to={moduleConfig.module.route.sheet(item.id)}
        className={cn('p-1 rounded-lg transition-colors', colors.primaryFg, colors.interactiveHover)}
        onClick={(e) => e.stopPropagation()}
        title="Edit MGA sheet"
      >
        <PencilIcon className="w-5 h-5" />
      </Link>
    )}
    {canDelete && item.status !== 'CLOSED' && (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDelete(item.id);
        }}
        className={cn(
          'p-1 rounded-lg transition-colors',
          isDark ? 'text-red-400 hover:bg-red-950/40' : 'text-red-600 hover:bg-red-50',
        )}
        title="Delete"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
          />
        </svg>
      </button>
    )}
  </div>
  );
};

export const getTableActions = (
  item: MgaPeriod,
  onDelete: (id: number) => void,
  _c?: unknown,
  _ctx?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean },
): React.ReactNode => (
  <TableActionsCell
    item={item}
    onDelete={onDelete}
    canDelete={options?.canDelete !== false}
    canEdit={options?.canEdit !== false}
  />
);

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  if (iconName === 'add') {
    return (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
      </svg>
    );
  }
  if (iconName === 'view') {
    return (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
    );
  }
  if (iconName === 'settings') {
    return (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
        />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    );
  }
  return null;
};

export type ModuleConfig = typeof moduleConfig;
